import { CSSProperties, MouseEvent } from 'react';

import Icon from '~/src/common/components/Icon';
import Link from '~/src/common/components/Link';
import dayjs from '~/src/common/services/Date';
import I18n from '~/src/common/services/I18n';
import { showNiceModal } from '~/src/common/services/ModalsManager';
import Tracker, { DELIVERY_ORDER_STATUS } from '~/src/common/services/Tracker';
import {
  daysDiff,
  getDateWithTimeslot,
  getFormattedDate,
  getTimeslotIntervalHourLabelInfos,
} from '~/src/common/utils/date';
import { getDeliveryIconName, getDeliveryLabelName } from '~/src/common/utils/delivery';
import { CurrentOrders200ItemsItem } from '~/src/queries/api-ecom/generated/api-ecom.schemas';
import { isDelivery } from '~/src/screens/App/CatalogLayout/utils';

import styles from './InProgressOrdersBanner.module.scss';
import { useEstimatedTimeSlot } from './useEstimatedTimeSlot.hook';
import { getSingleOrderSpinnerText, getSingleOrderTitle } from './utils';

interface Props {
  orders: CurrentOrders200ItemsItem[];
  style?: CSSProperties;
}

const InProgressOrdersBanner = ({ orders, style }: Props) => {
  // Prochaine commande
  const [order] = orders;
  const { delivery, delay } = order;
  const { deliveryZone, timeSlot } = delivery;

  // Délai de livraison pour la 1ere commande
  const estimatedTimeSlot = useEstimatedTimeSlot(order.id, delivery);
  const [from = timeSlot.from, to = timeSlot.to] = estimatedTimeSlot ?? [];
  const displayDelay = delay?.duration != null && estimatedTimeSlot == null;

  // Lien dépendant du nombre de commandes
  const isSingleOrder = orders.length === 1;
  const href = isSingleOrder ? `/account/order/${order.id}` : '/account/orders';

  const handleClick = (event: MouseEvent) => {
    // Ouverture du panel si commande en livraison
    if (isDelivery(delivery)) {
      void showNiceModal('delivery-tracking-drawer');
      event.stopPropagation();
      event.preventDefault();
    }
    // Tracking
    if (isSingleOrder) {
      const toTime = dayjs(to).format('HH[h]mm');
      const fromTime = dayjs(from).format('HH[h]mm');
      Tracker.sendEvent('click order tracking panel', {
        'shipping type': deliveryZone?.type === 'delivery' ? 'delivery' : 'pickup',
        'pickup mode': deliveryZone?.pickupType,
        'shipping slot': `${fromTime}-${toTime}`,
        'shipping slot days': daysDiff(from),
        'cart id': order.id,
        'order status': DELIVERY_ORDER_STATUS[order.state],
      });
    } else {
      Tracker.sendEvent('click several orders tracking panel');
    }
  };

  return (
    <Link href={href} onClick={handleClick} className={styles.container} style={style}>
      {isSingleOrder ? (
        // Une seule commande dans la banniere
        <>
          <div className={styles.spinner}>{getSingleOrderSpinnerText(order.state)}</div>
          <h4 className={styles.title}>{getSingleOrderTitle(order.state)}</h4>
          <span className={styles.details}>
            {order.completableUntil ? (
              // Commande complétable
              <>
                <Icon name="basket-normal" />
                {I18n.t('in-progress-orders-banner.complete-order.label')}
                <b>{getDateWithTimeslot(order.completableUntil)}</b>
              </>
            ) : (
              // Commande ayant passé la prépa, non complétable
              <>
                <Icon name={getDeliveryIconName(deliveryZone?.pickupType)} />
                <span>
                  {isDelivery(delivery)
                    ? I18n.t(`common.${getDeliveryLabelName(deliveryZone?.pickupType)}`)
                    : I18n.t('common.pickup')}
                </span>
                {' — '}
                <span>{getFormattedDate(from)}</span>
                {' — '}
                <span style={{ textDecoration: displayDelay ? 'line-through' : 'none' }}>
                  {getTimeslotIntervalHourLabelInfos(from, to)}
                </span>
                {displayDelay ? (
                  <strong>{getTimeslotIntervalHourLabelInfos(from, to, delay.duration)}</strong>
                ) : null}
              </>
            )}
          </span>
        </>
      ) : (
        // Plusieurs commandes dans la banniere
        <>
          <div className={styles.spinner}>{orders.length}</div>
          <h4 className={styles.title}>
            {I18n.t('home.order.several-in-progress', { count: orders.length })}
          </h4>
        </>
      )}
    </Link>
  );
};

export default InProgressOrdersBanner;
